import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import './AdminLearningTimePage.css';

interface UserResult {
    id: string;
    email: string;
    username: string;
    fullName: string | null;
    avatarUrl: string | null;
    role: string;
}

interface LearningTimeItem {
    courseId: string;
    courseTitle: string;
    courseTitles: Record<string, string> | null;
    courseThumbnailUrl: string | null;
    totalSeconds: number;
}

interface LearningTimeResponse {
    items: LearningTimeItem[];
    totalSeconds: number;
    period: string;
}

type Period = 'week' | 'month' | 'all';

const AdminLearningTimePage: React.FC = () => {
    const { t, i18n } = useTranslation(['admin', 'profile']);
    const navigate = useNavigate();
    const { user } = useAuth();

    // User search
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<UserResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Selected user
    const [selectedUser, setSelectedUser] = useState<UserResult | null>(null);

    // Learning time data
    const [period, setPeriod] = useState<Period>('all');
    const [data, setData] = useState<LearningTimeResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const lang = i18n.language;

    // Click outside to close dropdown
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Debounced user search
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (searchQuery.length < 2) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await apiClient.get<UserResult[]>(
                    '/profiles/admin/users/search',
                    { params: { q: searchQuery } },
                );
                setSearchResults(res.data);
                setShowDropdown(true);
            } catch {
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 300);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [searchQuery]);

    // Fetch learning time when user or period changes
    const fetchData = useCallback(async (userId: string, p: Period) => {
        setLoading(true);
        try {
            const res = await apiClient.get<LearningTimeResponse>(
                `/profiles/admin/${userId}/learning-time`,
                { params: { period: p } },
            );
            setData(res.data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedUser) {
            fetchData(selectedUser.id, period);
        }
    }, [selectedUser, period, fetchData]);

    const handleSelectUser = (u: UserResult) => {
        setSelectedUser(u);
        setSearchQuery(u.username);
        setShowDropdown(false);
    };

    const handlePeriodChange = (p: Period) => {
        setPeriod(p);
    };

    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        if (minutes >= 60) {
            return t('profile:stats.timeShortHoursMinutes', {
                h: Math.floor(minutes / 60),
                m: minutes % 60,
            });
        }
        return t('profile:stats.timeShortMinutes', { value: minutes });
    };

    const getCourseTitle = (item: LearningTimeItem): string => {
        if (item.courseTitles && item.courseTitles[lang]) {
            return item.courseTitles[lang];
        }
        return item.courseTitle;
    };

    const periods: { key: Period; label: string }[] = [
        { key: 'week', label: t('admin:learningTime.thisWeek', 'This Week') },
        { key: 'month', label: t('admin:learningTime.thisMonth', 'This Month') },
        { key: 'all', label: t('admin:learningTime.allTime', 'All Time') },
    ];

    return (
        <div className="admin-lt-page">
            <div className="admin-lt-header">
                <button className="admin-lt-back" onClick={() => navigate(-1)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1>{t('admin:learningTime.title', 'Learning Time Analytics')}</h1>
            </div>

            {/* User Search */}
            <div className="admin-lt-search-section" ref={searchRef}>
                <label className="admin-lt-label">
                    {t('admin:learningTime.selectUser', 'Select User')}
                </label>
                <div className="admin-lt-search-box">
                    <svg className="admin-lt-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.3-4.3" />
                    </svg>
                    <input
                        type="text"
                        className="admin-lt-search-input"
                        placeholder={t('admin:learningTime.searchPlaceholder', 'Search by email or username...')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                    />
                    {searching && <div className="admin-lt-search-spinner" />}
                </div>

                {showDropdown && searchResults.length > 0 && (
                    <div className="admin-lt-dropdown">
                        {searchResults.map((u) => (
                            <button
                                key={u.id}
                                className={`admin-lt-dropdown-item ${selectedUser?.id === u.id ? 'active' : ''}`}
                                onClick={() => handleSelectUser(u)}
                            >
                                <div className="admin-lt-user-avatar">
                                    {u.avatarUrl ? (
                                        <img src={u.avatarUrl} alt="" />
                                    ) : (
                                        <span>{u.username.charAt(0).toUpperCase()}</span>
                                    )}
                                </div>
                                <div className="admin-lt-user-info">
                                    <span className="admin-lt-user-name">{u.username}</span>
                                    <span className="admin-lt-user-email">{u.email}</span>
                                </div>
                                <span className="admin-lt-user-role">{u.role}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Selected User Card */}
            {selectedUser && (
                <div className="admin-lt-selected-user">
                    <div className="admin-lt-user-avatar large">
                        {selectedUser.avatarUrl ? (
                            <img src={selectedUser.avatarUrl} alt="" />
                        ) : (
                            <span>{selectedUser.username.charAt(0).toUpperCase()}</span>
                        )}
                    </div>
                    <div>
                        <div className="admin-lt-selected-name">
                            {selectedUser.fullName || selectedUser.username}
                        </div>
                        <div className="admin-lt-selected-email">{selectedUser.email}</div>
                    </div>
                </div>
            )}

            {/* Period Tabs + Data */}
            {selectedUser && (
                <>
                    <div className="admin-lt-tabs">
                        {periods.map((p) => (
                            <button
                                key={p.key}
                                className={`admin-lt-tab ${period === p.key ? 'active' : ''}`}
                                onClick={() => handlePeriodChange(p.key)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    {/* Total */}
                    {data && !loading && (
                        <div className="admin-lt-total">
                            <span className="admin-lt-total-label">
                                {t('admin:learningTime.total', 'Total')}
                            </span>
                            <span className="admin-lt-total-value">
                                {formatTime(data.totalSeconds)}
                            </span>
                        </div>
                    )}

                    {/* Course List */}
                    <div className="admin-lt-list">
                        {loading ? (
                            <div className="admin-lt-loading">
                                <div className="loading-spinner" />
                            </div>
                        ) : data && data.items.length > 0 ? (
                            data.items.map((item) => (
                                <div key={item.courseId} className="admin-lt-course-row">
                                    <div className="admin-lt-course-thumb">
                                        {item.courseThumbnailUrl ? (
                                            <img src={item.courseThumbnailUrl} alt="" />
                                        ) : (
                                            <div className="admin-lt-course-thumb-placeholder">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    <div className="admin-lt-course-info">
                                        <span className="admin-lt-course-title">
                                            {getCourseTitle(item)}
                                        </span>
                                    </div>
                                    <span className="admin-lt-course-time">
                                        {formatTime(item.totalSeconds)}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="admin-lt-empty">
                                <p>{t('admin:learningTime.noData', 'No learning time recorded for this period')}</p>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminLearningTimePage;
